import type { IglooFloorId, IglooStyle, IglooWallId } from "@clubap/shared";
import { useEffect, useState } from "react";
import type { IglooSnapshot } from "../economy/igloo.js";

interface Props {
  snapshot: IglooSnapshot;
  /** Guardar el estilo. A qué cuarto va lo decide el adaptador de `App`, no este panel. */
  onApply: (floor: IglooFloorId, wall: IglooWallId) => Promise<IglooStyle>;
  /** El estilo se aplicó: el cuarto se repinta y el servidor ya lo sabe. */
  onApplied: (style: IglooStyle) => void;
  onClose: () => void;
}

/**
 * Piso y pared del iglú.
 *
 * Se elige por MUESTRA y no por nombre: lo que se decide acá es un color, y una lista de
 * palabras obliga a probarlas una por una para ver cuál es cuál. Cada muestra se pinta
 * con los mismos dos colores que va a usar el escenario, así que lo que ves en el botón
 * es lo que vas a tener en el cuarto.
 *
 * Se aplica al instante, sin botón de confirmar: es aspecto, no cuesta nada y se deshace
 * eligiendo otro. Un "guardar" acá sería una pregunta de más.
 */
export function IglooStylePanel({ snapshot, onApply, onApplied, onClose }: Props) {
  const [style, setStyle] = useState<IglooStyle>(snapshot.style);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const apply = async (next: IglooStyle) => {
    const previo = style;
    // Se pinta en el acto y se corrige si el servidor dice que no: el viaje dura lo que
    // dure, y esperar a que vuelva para enseñar un color se siente roto.
    setStyle(next);
    setBusy(true);
    setError(null);

    try {
      const guardado = await onApply(next.floor, next.wall);
      onApplied(guardado);
    } catch (cause) {
      setStyle(previo);
      setError(cause instanceof Error ? cause.message : "No se pudo cambiar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay">
      <div className="panel sheet">
        <div className="sheet__head">
          <h2 className="sheet__title">Piso y pared</h2>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </div>

        {error && (
          <p className="sheet__error" role="alert">
            {error}
          </p>
        )}

        <section className="sheet__group">
          <h3 className="sheet__slot">Piso</h3>
          <div className="estilos">
            {snapshot.floors.map((floor) => (
              <button
                className={`estilo${style.floor === floor.id ? " estilo--puesto" : ""}`}
                type="button"
                key={floor.id}
                disabled={busy}
                onClick={() => void apply({ ...style, floor: floor.id })}
              >
                <span
                  className="estilo__muestra"
                  style={{
                    background: floor.cuadros
                      ? `repeating-conic-gradient(${floor.trim} 0% 25%, ${floor.color} 0% 50%) 0 / 16px 16px`
                      : floor.color,
                    borderColor: floor.trim,
                  }}
                  aria-hidden="true"
                />
                {floor.name}
              </button>
            ))}
          </div>
        </section>

        <section className="sheet__group">
          <h3 className="sheet__slot">Pared</h3>
          <div className="estilos">
            {snapshot.walls.map((wall) => (
              <button
                className={`estilo${style.wall === wall.id ? " estilo--puesto" : ""}`}
                type="button"
                key={wall.id}
                disabled={busy}
                onClick={() => void apply({ ...style, wall: wall.id })}
              >
                <span
                  className="estilo__muestra"
                  style={{
                    background: `linear-gradient(${wall.color} 70%, ${wall.trim} 70%)`,
                    borderColor: wall.trim,
                  }}
                  aria-hidden="true"
                />
                {wall.name}
              </button>
            ))}
          </div>
        </section>

        <p className="sheet__hint">
          Sólo cambia cómo se ve. Los muebles se quedan donde están.
        </p>
      </div>
    </div>
  );
}
